import { useLayoutEffect, useMemo, useRef, type RefObject } from 'react'
import { useOptionalCommands } from '../context/CommandContext'
import { adminNavigationCommands } from '../services/navigationCommands'
import { resolveAdminTabAlias, type AdminTab } from '../components/workspaces/admin/navigationRegistry'
import type { CommandContext, CommandDefinition } from '../services/commandRegistry'

const usable = (element: HTMLInputElement | HTMLButtonElement | null) => Boolean(
  element?.isConnected && !element.disabled && element.getClientRects().length,
)

/** Safe pilot adapter: page-owned controls/handlers, never a domain action catalog. */
export function useListCommands(options: {
  page: Extract<AdminTab, 'locations' | 'workers' | 'jobs'>
  enabled: boolean
  search?: RefObject<HTMLInputElement | null>
  actions?: readonly { id: string; label: string; available?: () => boolean; execute: () => void | boolean }[]
  filters?: RefObject<HTMLButtonElement | null>
  refresh?: () => Promise<boolean>
  refreshReady?: boolean
}) {
  const commands = useOptionalCommands()
  const live = useRef(options)
  live.current = options
  const definitions = useMemo<CommandDefinition[]>(() => {
    const active = (context: CommandContext) => {
      const current = live.current
      const route = context.navigation?.route
      return current.enabled && context.workspace === 'admin' && Boolean(route)
        && resolveAdminTabAlias(new URL(route!, 'http://punch.invalid').searchParams.get('workspace')) === current.page
        && adminNavigationCommands(context, { t: key => key }).some(command => command.id === `navigation.admin.${current.page}`)
    }
    const result: CommandDefinition[] = options.search ? [{
      id: `context.${options.page}.search`, label: 'Focus page search', category: 'Current page',
      remember: true,
      keywords: ['Search'], workspaces: ['admin'],
      available: context => active(context) && usable(live.current.search?.current || null),
      execute: () => { live.current.search?.current?.focus() },
    }] : []
    if (options.filters) result.push({
      id: `context.${options.page}.filters`, label: 'Open page filters', category: 'Current page',
      remember: true,
      keywords: ['Filters'], workspaces: ['admin'],
      available: context => active(context) && usable(live.current.filters?.current || null),
      execute: () => {
        const button = live.current.filters?.current
        if (button?.getAttribute('aria-expanded') === 'false') button.click()
        button?.focus()
      },
    })
    if (options.refresh) result.push({
      id: `context.${options.page}.refresh`, label: 'Refresh current list', category: 'Current page',
      remember: true, workspaces: ['admin'],
      available: context => active(context) && live.current.refreshReady !== false && usable(live.current.search?.current || null),
      execute: () => live.current.refresh?.() ?? false,
    })
    for (const action of options.actions || []) result.push({
      id: `context.${options.page}.${action.id}`, label: action.label, category: 'Current page',
      workspaces: ['admin'], remember: true,
      available: context => active(context) && (live.current.actions?.find(item=>item.id===action.id)?.available?.() ?? false),
      execute: () => {
        const current = live.current.actions?.find(item=>item.id===action.id)
        return current ? current.execute() !== false : false
      },
    })
    return result
  }, [options.page, Boolean(options.search), Boolean(options.filters), Boolean(options.refresh), options.actions?.map(action=>action.id).join('|')])
  useLayoutEffect(() => commands?.context && options.enabled ? commands.registry.register(definitions) : undefined,
    [commands?.registry, Boolean(commands?.context), definitions, options.enabled, options.refreshReady])
}
